/**
 * Laravel Echo client for the Reverb (Pusher-protocol) WebSocket server.
 *
 * Lazily initialized on first call so we don't open a socket until a
 * component actually needs to subscribe. The auth callback is fired
 * per channel subscription, so it always reads the *current* Bearer
 * token from authState — even if Echo was constructed before login.
 */
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import axios from "axios";
import { authToken } from "@/state/api/authState";
import { getApiBaseUrl, getReverbConfig } from "@/config/runtime";

(window as any).Pusher = Pusher;

let echoInstance: Echo<any> | null = null;

const apiBase = getApiBaseUrl().replace(/\/+$/, "");
const { host: wsHost, port: wsPort, key: wsKey, scheme: wsScheme } =
	getReverbConfig();
const forceTLS = wsScheme === "https";

export function getEcho(): Echo<any> {
	if (echoInstance) return echoInstance;

	echoInstance = new Echo({
		broadcaster: "reverb",
		key: wsKey,
		wsHost,
		wsPort,
		wssPort: wsPort,
		forceTLS,
		enabledTransports: forceTLS ? ["wss"] : ["ws"],
		// pusher-js envoie sinon des beacons à stats.pusher.com (endpoint
		// `engage`) qui sont inutiles avec Reverb auto-hébergé et bloqués
		// par les adblockers → "blocked:other" dans la console.
		disableStats: true,
		// `authorizer` is called per channel subscription, so the token is read
		// fresh from authState — fixes 401 when Echo was lazy-imported before login.
		authorizer: (channel: any) => ({
			authorize: (socketId: string, callback: (error: boolean, data: any) => void) => {
				const token = authToken.value?.key || "";
				if (!token) {
					callback(true, new Error("[echo] no auth token in state"));
					return;
				}
				axios
					.post(
						`${apiBase}/broadcasting/auth`,
						{ socket_id: socketId, channel_name: channel.name },
						{
							headers: {
								Authorization: `Bearer ${token}`,
								Accept: "application/json",
							},
						},
					)
					.then((response) => callback(false, response.data))
					.catch((error) => callback(true, error));
			},
		}),
	});

	return echoInstance;
}

export function disconnectEcho(): void {
	if (echoInstance) {
		echoInstance.disconnect();
		echoInstance = null;
	}
}
